import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { FaCircle } from 'react-icons/fa';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

const RedemptionsOffer = ({ data }) => {
  const {
    redemptionSummary,
    redemptionOverTime,
    recentRedemptions,
    redemptionByStatus,
    redemptionByChannel,
    userBreakdown,
    redemptionInsights,
  } = data;

  const currencyEGP = (n) => `${Number(n).toLocaleString()} EGP`;

  const SectionNumber = ({ n }) => (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: `1.5px solid #3FBEAC`,
        color: '#3FBEAC',
        bgcolor: '#ECF9F7',
        fontSize: 12,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );

  const Field = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );

  const TrendChart = ({ chartData }) => (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="redemptionOverTimeFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3FBEAC" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#3FBEAC" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
        <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#8A94A6' }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#8A94A6' }} />
        <Tooltip
          formatter={(value) => [value, 'Redemptions']}
          contentStyle={{ borderRadius: 8, border: '1px solid #E8E8E8', fontSize: 12 }}
        />
        <Area
          type="monotone"
          dataKey="redemptions"
          stroke="#1A1F2B"
          strokeWidth={2}
          fill="url(#redemptionOverTimeFill)"
          dot={{ r: 4, stroke: '#1A1F2B', strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      {/* Left column */}
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, width: '100%', height: '100%' }}>
            {/* Redemption Summary */}
            <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
              <Box display="flex" gap={1.5} alignItems="center">
                <SectionNumber n={1} />
                <Typography fontWeight={600}>Redemption Summary</Typography>
              </Box>
              <Box sx={{ p: { xs: 1, sm: 2 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Field label="Total Redemption" value={redemptionSummary.totalRedemption.toLocaleString()} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Field label="Unique Users" value={redemptionSummary.uniqueUsers.toLocaleString()} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Field label="Average Order Value" value={currencyEGP(redemptionSummary.averageOrderValue)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Field label="Total Discounted Amount" value={currencyEGP(redemptionSummary.totalDiscountedAmount)} />
                  </Grid>
                  <Grid item xs={6} sm={3} sx={{ mt: 2.5 }}>
                    <Field label="Total Sales Generated" value={currencyEGP(redemptionSummary.totalSalesGenerated)} />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Redemption Over Time */}
            <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
              <Box display="flex" alignItems="center"  justifyContent="space-between" sx={{ px: 1, py: 0.5, my: 0.5, justifyContent:{xs:"normal", md:"space-between"} , flexDirection:{xs:"column", md:"row"}, gap:{xs:2, md: 0}, alignItems:{xs:"flex-start", md:"center"}}}>
                <Box display="flex" gap={1.5} alignItems="center">
                  <SectionNumber n={2} />
                  <Typography fontWeight={600}>Redemption Over Time</Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{flexDirection:{xs:"column", md:"row"}, gap:{xs:2, md: 0}, width:{xs:"100%", md:"auto"}}}>
                  <FormControl size="small" sx={{ width:{xs:"100%", md:"auto"}, '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 } }}>
                    <Select defaultValue="daily" IconComponent={KeyboardArrowDownIcon}>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                              <DemoContainer components={["DatePicker"]} sx={{ pt: 0, "& .MuiFormControl-root": { width: "100%" } }}>
                                                  <DatePicker
                                                      sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14, overflow:"hidden" } }}
                                                      format="DD,MM,YYYY"
                                                      slotProps={{ textField: { size: "small", placeholder: "Select Date", fullWidth: true } }}
                                                  />
                                              </DemoContainer>
                                          </LocalizationProvider>
                </Stack>
              </Box>
              <TrendChart chartData={redemptionOverTime.data} />
            </Box>

            {/* Recent Redemptions */}
            <Box my={1} py={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, my: 0.5 }}>
                <Box display="flex" gap={1.5} alignItems="center">
                  <SectionNumber n={3} />
                  <Typography fontWeight={600}>Recent Redemptions</Typography>
                </Box>
                <Link
                  sx={{
                    color: '#159D89',
                    textDecorationLine: 'none',
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  View all <IoIosArrowRoundForward size={18} />
                </Link>
              </Box>

              <Box sx={{ overflow: 'auto' , border: "1px solid #F2F2F2", borderRadius: 3, my:1, height:400}}>
                <Table sx={{height:"100%"}}  size="small" >
                  <TableHead>
                    <TableRow>
                      {['ID', 'User', 'Date & Time', 'Order ID', 'Order Value', 'Discount Value', 'Status', 'Entry Method'].map((h) => (
                        <TableCell key={h} sx={{ color: '#535353', fontSize: 12, borderBottom: '1px solid #E8E8E8', textAlign:"center", width:150 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRedemptions.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:100 }}>{row.id}</TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:150 }}>{row.user}</TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:180 }}>{row.dateTime}</TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:150 }}>{row.orderId}</TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:150 }}>{currencyEGP(row.orderValue)}</TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:180}}>{currencyEGP(row.discountValue)}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2',textAlign:"center", width:50 }}>
                          <Chip
                            size="small"
                            label={row.status}
                            sx={{ bgcolor: '#ECF9F7', fontWeight: 600, fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign:"center", width:150 }}>{row.entryMethod}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
        </Card>
      </Grid>

      {/* Right column */}
      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
        {/* Redemption by Status */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5 }}>
          <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
            <Typography fontWeight={600}>Redemption by Status</Typography>
          </Box>
          <Box sx={{ p: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Box sx={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={redemptionByStatus.segments}
                      dataKey="value"
                      nameKey="label"
                      innerRadius={45}
                      outerRadius={62}
                      paddingAngle={2}
                      startAngle={90}
                      endAngle={-270}
                    >
                      {redemptionByStatus.segments.map((seg) => (
                        <Cell key={seg.label} fill={seg.color} stroke="none" />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
                    {redemptionByStatus.total}
                  </Typography>
                  <Typography variant="caption" color="#535353">
                    Total
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={1} flex={1} sx={{ width: '100%' }}>
                {redemptionByStatus.segments.map((seg) => (
                  <Stack key={seg.label} direction="row" justifyContent="space-between" alignItems="center" py={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FaCircle size={8} color={seg.color} />
                      <Typography variant="body2">{seg.label}</Typography>
                    </Stack>
                    <Typography variant="body2" color="#535353">
                      ({seg.value.toLocaleString()}) {seg.percent}%
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Card>

        {/* Redemption by Channel */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, my: 2 }}>
          <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
            <Typography fontWeight={600}>Redemption by Channel</Typography>
          </Box>
          <Stack divider={<Divider />}>
            {redemptionByChannel.map((ch, i) => (
              <Stack key={ch.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: '1px solid #E8E8E8',
                      color: '#535353',
                      fontSize: 11,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{ch.label}</Typography>
                </Stack>
                <Typography variant="body2" fontWeight={600}>
                  {ch.value.toLocaleString()} <Typography component="span" variant="body2" color="#535353">({ch.percent}%)</Typography>
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* User Breakdown */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, my: 2.3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" borderBottom="1px solid #E8E8E8" py={1} my={0.5}>
            <Typography fontWeight={600}>User Breakdown</Typography>
            <Link
              sx={{
                color: '#159D89',
                textDecorationLine: 'none',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
              }}
            >
              View Full Report <IoIosArrowRoundForward size={18} />
            </Link>
          </Box>
          <Stack divider={<Divider />}>
            {userBreakdown.map((u) => (
              <Stack key={u.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box width={30} height={30} borderRadius={"50%"} border={"1px solid #E8E8E8"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                       {u.icon}
                  </Box>
                  <Typography variant="body2">{u.label}</Typography>
                </Stack>
                <Typography variant="body2" fontWeight={600}>
                  {u.percent}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* Insights */}
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, mt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" borderBottom="1px solid #E8E8E8" py={1} my={0.5}>
            <Typography fontWeight={600}>Insights</Typography>
            <Link
              sx={{
                color: '#159D89',
                textDecorationLine: 'none',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
              }}
            >
              View More Insights <IoIosArrowRoundForward size={18} />
            </Link>
          </Box>
          <Stack spacing={1.25} sx={{ py: 1, flexGrow: 1 }}>
            {redemptionInsights.map((text, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" py={2} borderBottom={ i !== redemptionInsights.length - 1 ? "1px solid #E8E8E8" : "none" }>
                <FaCircle size={6} color="#3FBEAC" style={{ marginTop: 6, flexShrink: 0 }} />
                <Typography variant="body2">{text}</Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default RedemptionsOffer;